import React from "react";

const ERROR_MESSAGES_BY_CODE = {
  BASE_LANGUAGE:
    "This is the project's base language and cannot be removed.",
  FORBIDDEN: "You don't have permission to remove this language.",
  NOT_ATTACHED: "This language is no longer attached to the project.",
};
const GENERIC_ERROR_MESSAGE =
  "Could not remove language. Please try again.";

const RemoveLanguage = ({
  language,
  onCancel,
  onConfirm,
  translationsToDelete,
  countLoading = false,
  countError = false,
  submitting = false,
  submitErrorCode = null,
  hasSubmitError = false,
}) => {
  const showCount =
    !countLoading && !countError && typeof translationsToDelete === "number";
  const confirmDisabled = countLoading || submitting;
  const errorMessage = hasSubmitError
    ? ERROR_MESSAGES_BY_CODE[submitErrorCode] ?? GENERIC_ERROR_MESSAGE
    : null;
  return (
    <div
      className="fixed z-10 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        />

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg
                  className="h-6 w-6 text-red-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3
                  className="text-lg leading-6 font-medium text-gray-900"
                  id="modal-title"
                >
                  Remove language
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to remove{" "}
                    <span className="font-bold">{language.name}</span> (
                    <span className="font-bold uppercase">{language.iso}</span>)
                    from this project?
                  </p>
                  <p
                    className="mt-2 text-sm text-red-600"
                    data-testid="removal-warning"
                  >
                    {countLoading && (
                      <span
                        role="status"
                        aria-live="polite"
                        className="inline-flex items-center gap-2"
                      >
                        <svg
                          className="animate-spin h-3 w-3 text-red-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                        Counting translations…
                      </span>
                    )}
                    {showCount && (
                      <>
                        This will permanently delete{" "}
                        <span className="font-bold">
                          {translationsToDelete}
                        </span>{" "}
                        translation
                        {translationsToDelete === 1 ? "" : "s"} in{" "}
                        <span className="font-bold">{language.name}</span>.
                      </>
                    )}
                    {!countLoading && countError && (
                      <>
                        Translations in this language will be permanently
                        deleted.
                      </>
                    )}
                  </p>
                  {errorMessage && (
                    <p
                      className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                      role="alert"
                      data-testid="removal-error"
                    >
                      {errorMessage}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={() => onConfirm()}
              disabled={confirmDisabled}
              aria-busy={submitting || undefined}
              className="w-full inline-flex justify-center items-center gap-2 rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting && (
                <svg
                  data-testid="remove-confirm-spinner"
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
              )}
              {submitting ? "Removing…" : "Remove"}
            </button>
            <button
              type="button"
              onClick={() => onCancel()}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoveLanguage;
